import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Play, ChevronDown, AlertCircle } from 'lucide-react';

export const Hero = ({ latestAlbum, onPlayAlbum }) => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({
        x: (e.clientX / window.innerWidth - 0.5) * 30,
        y: (e.clientY / window.innerHeight - 0.5) * 30,
      });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const hasPreview = latestAlbum?.tracks?.some(t => t.previewUrl);

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Cinematic Background */}
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-to-b from-background via-charcoal to-background" />
        
        {/* Parallax image */}
        <motion.div
          className="absolute inset-0 opacity-40"
          style={{ x: mousePosition.x * 0.5, y: mousePosition.y * 0.5 }}
        >
          <div
            className="absolute inset-0 bg-cover bg-center scale-110"
            style={{
              backgroundImage: `url('https://images.unsplash.com/photo-1510387186859-9a76f76aff62?w=1920&q=80')`,
            }}
          />
        </motion.div>
        
        {/* Cinematic overlays */}
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/60 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-r from-background/70 via-transparent to-background/70" />
        
        {/* Crimson glow */}
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-crimson/10 rounded-full blur-[150px]" />
      </div>

      <div className="absolute inset-0 z-10 grain" />
      <div className="absolute inset-0 z-10 vignette" />

      {/* Content */}
      <div className="relative z-20 max-w-6xl mx-auto px-4 sm:px-6 text-center">
        {/* Pre-title */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.3 }}
          className="mb-6 sm:mb-8"
        >
          <span className="inline-flex items-center gap-3 sm:gap-4 text-[10px] font-body uppercase tracking-[0.3em] sm:tracking-[0.4em] text-crimson">
            <span className="w-8 sm:w-12 h-px bg-crimson/50" />
            A Cinematic Sound Universe
            <span className="w-8 sm:w-12 h-px bg-crimson/50" />
          </span>
        </motion.div>

        {/* Main Title */}
        <motion.h1
          initial={{ opacity: 0, y: 40, filter: 'blur(10px)' }}
          animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
          transition={{ duration: 1.2, delay: 0.5 }}
          className="font-display text-5xl sm:text-7xl lg:text-9xl text-ivory leading-[0.85] mb-8 sm:mb-10"
        >
          <span className="block">SUGO</span>
          <span className="block text-gradient-crimson italic">SOUND</span>
        </motion.h1>

        {/* Tagline */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.9 }}
          className="font-body text-sm sm:text-base lg:text-lg text-ivory/60 max-w-xl mx-auto mb-10 sm:mb-12 tracking-wide"
        >
          Italian soul. Latin rhythm. Nightlife energy.
          <br className="hidden sm:block" />
          Enter the world where every beat tells a story.
        </motion.p>

        {/* CTA Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.1 }}
          className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-6"
        >
          <Button 
            variant="neon" 
            size="lg" 
            className="group min-w-[180px] sm:min-w-[200px]"
            onClick={() => latestAlbum && onPlayAlbum(latestAlbum)}
            disabled={!hasPreview}
          >
            <Play className="w-4 h-4 sm:w-5 sm:h-5 mr-2 group-hover:scale-110 transition-transform" />
            Listen Now
          </Button>
          <Button variant="neonOutline" size="lg" className="min-w-[180px] sm:min-w-[200px]" asChild>
            <a href="#drops">Explore Drops</a>
          </Button>
        </motion.div>

        {/* Latest Release Card */}
        {latestAlbum && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 1.4 }}
            className="mt-16 sm:mt-20"
          >
            <button 
              onClick={() => hasPreview && onPlayAlbum(latestAlbum)}
              disabled={!hasPreview}
              className="inline-flex items-center gap-4 sm:gap-5 px-4 sm:px-6 py-3 sm:py-4 border border-crimson/20 bg-background/50 backdrop-blur-sm hover:border-crimson/40 hover:bg-crimson/5 transition-all duration-400 group disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <div className="relative w-14 h-14 sm:w-16 sm:h-16 overflow-hidden">
                <img
                  src={latestAlbum.coverArtUrl}
                  alt={latestAlbum.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-600"
                />
                <div className="absolute inset-0 bg-crimson/20 opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
              <div className="text-left">
                <p className="text-[10px] uppercase tracking-[0.3em] text-crimson font-body mb-1.5 sm:mb-2">New Drop</p>
                <p className="text-base sm:text-lg font-display text-ivory mt-1">{latestAlbum.title}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{latestAlbum.trackCount} Tracks</p>
              </div>
              {hasPreview ? (
                <div className="ml-2 sm:ml-4 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-crimson flex items-center justify-center shadow-neon group-hover:scale-110 transition-transform">
                  <Play className="w-4 h-4 sm:w-5 sm:h-5 text-primary-foreground ml-0.5" />
                </div>
              ) : (
                <div className="ml-2 sm:ml-4 flex items-center gap-1 text-muted-foreground">
                  <AlertCircle className="w-4 h-4" />
                  <span className="text-[10px] uppercase tracking-wider">Soon</span>
                </div>
              )}
            </button>
          </motion.div>
        )}
      </div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2 }}
        className="absolute bottom-6 sm:bottom-8 left-1/2 -translate-x-1/2 z-20"
      >
        <motion.a
          href="#drops"
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="flex flex-col items-center gap-2 text-muted-foreground hover:text-crimson transition-colors"
        >
          <span className="text-[10px] uppercase tracking-[0.3em] font-body">Scroll</span>
          <ChevronDown className="w-4 h-4" />
        </motion.a>
      </motion.div>
    </section>
  );
};
